import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../utils/supabaseClient';

const TrackComplaint = () => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [ticketNo, setTicketNo] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    if (!fullName.trim() && !phone.trim() && !ticketNo.trim()) {
      alert('กรุณากรอกข้อมูลอย่างน้อย 1 ช่อง');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      let query = supabase
        .from('complaints')
        .select(`
          *,
          complaint_events (
            id,
            status,
            description,
            created_at
          )
        `);

      // Build search conditions
      if (ticketNo.trim()) {
        query = query.eq('ticket_no', ticketNo.trim());
      } else {
        const conditions = [];
        if (fullName.trim()) {
          conditions.push(`full_name.ilike.%${fullName.trim()}%`);
        }
        if (phone.trim()) {
          conditions.push(`contact_number.ilike.%${phone.trim()}%`);
        }
        if (conditions.length > 0) {
          query = query.or(conditions.join(','));
        }
      }

      const { data: complaints, error } = await query;

      if (error) {
        console.error('Search error:', error);
        throw new Error('เกิดข้อผิดพลาดในการค้นหา');
      }

      if (!complaints || complaints.length === 0) {
        setResult({ notFound: true });
      } else {
        const complaint = complaints[0];
        setResult({
          ticket_no: complaint.ticket_no,
          full_name: complaint.full_name,
          phone: complaint.contact_number,
          status: complaint.status,
          category: complaint.category,
          description: complaint.description,
          created_at: complaint.created_at,
          events: complaint.complaint_events?.map(event => ({
            time: new Date(event.created_at).toLocaleString('th-TH'),
            text: event.description,
            status: event.status
          })) || []
        });
      }
    } catch (error) {
      console.error('Search error:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        <BreadcrumbTrail />
        

        
        <div className="bg-card rounded-lg border border-border p-6">
          <h1 className="text-2xl font-bold mb-4">ติดตามสถานะคำร้อง</h1>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <Input label="ชื่อ-นามสกุล" value={fullName} onChange={(e)=>setFullName(e.target.value)} placeholder="เช่น สมชาย ใจดี" />
            <Input label="หมายเลขโทรศัพท์" value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="08X-XXX-XXXX" />
            <Input label="เลขที่คำร้อง" value={ticketNo} onChange={(e)=>setTicketNo(e.target.value)} placeholder="TH-YYYYMM-####" />
          </div>
          <Button onClick={handleSearch} disabled={loading}>
            <Icon name="Search" size={16} className="mr-2" />
            ค้นหา
          </Button>

          {result && (
            <div className="mt-6">
              {result.notFound ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertCircle" size={20} className="text-yellow-600" />
                    <p className="text-yellow-800">ไม่พบข้อมูลที่ค้นหา กรุณาตรวจสอบข้อมูลอีกครั้ง</p>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-accent/30 rounded border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm text-muted-foreground">เลขที่คำร้อง</p>
                      <p className="text-lg font-semibold">{result.ticket_no}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">สถานะ</p>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                        result.status === 'received' ? 'bg-blue-100 text-blue-700' :
                        result.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                        result.status === 'completed' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {result.status === 'received' ? 'รับเรื่องแล้ว' :
                         result.status === 'in_progress' ? 'อยู่ในระหว่างดำเนินการ' :
                         result.status === 'completed' ? 'ดำเนินการแล้วเสร็จ' : result.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-muted-foreground">ชื่อผู้ยื่น</p>
                      <p className="font-medium">{result.full_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">เบอร์โทร</p>
                      <p className="font-medium">{result.phone}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-muted-foreground">หมวดหมู่</p>
                    <p className="font-medium">{result.category}</p>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-muted-foreground">รายละเอียด</p>
                    <p className="text-sm bg-background p-3 rounded border">{result.description}</p>
                  </div>

                  <div>
                    <p className="font-medium mb-4">ไทม์ไลน์การดำเนินการ</p>
                    <div className="space-y-4">
                      {result.events?.map((e, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <div className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 ${
                            e.status === 'received' ? 'bg-blue-500' :
                            e.status === 'in_progress' ? 'bg-yellow-500' :
                            e.status === 'completed' ? 'bg-green-500' :
                            'bg-gray-500'
                          }`}></div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-medium text-foreground">
                                {e.status === 'received' ? 'รับเรื่องแล้ว' :
                                 e.status === 'in_progress' ? 'อยู่ในระหว่างดำเนินการ' :
                                 e.status === 'completed' ? 'ดำเนินการแล้วเสร็จ' :
                                 e.status}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {e.time}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg border">
                              {e.text}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrackComplaint;


